import { useState } from 'react';
import { Upload } from 'antd';
import { FileTextOutlined, InboxOutlined, LoadingOutlined, RobotOutlined, UploadOutlined } from '@ant-design/icons';
import { Sparkles } from 'lucide-react';

import { Button, Dialog, Field, Select, Textarea } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { dungLuongVN, soVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { DUOI_TEP_PHAN_CONG, HAN_MAC_DINH, NHAN_UU_TIEN, TOI_DA_MB, UU_TIEN_PHAN_CONG } from '../../constants';
import { usePhanCongTuTep, useTrichChuTep } from '../../hooks/usePhanCong';
import type { UuTienPhanCong } from '../../types-phan-cong';
import { LoiForm } from '../LoiForm';

const TRICH_DOAN = 300;
const thuGon = (s: string) => s.replace(/\s+/g, ' ').slice(0, TRICH_DOAN);
const duoi = (ten: string) => ten.split('.').pop()?.toLowerCase() ?? '';

/**
 * "Upload file & AI phân công" (dialog của bản cũ): chọn tệp, xem trích đoạn nội dung, chọn ưu tiên giao
 * và hạn mặc định, ghi chú cho AI; AI phân công chạy nền ở backend, dialog chờ tới khi xong rồi báo số
 * nhiệm vụ đã tạo. Đóng dialog lúc đang chờ thì chỉ thôi theo dõi, tác vụ nền vẫn chạy.
 *
 * Phía gọi gắn `key` mới mỗi lần mở để dialog bắt đầu trống.
 */
export function PhanCongTepDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, locale } = useT();
  const toast = useToast();
  const phanCong = usePhanCongTuTep();
  const trich = useTrichChuTep();
  const [tep, setTep] = useState<File | null>(null);
  const [loiTep, setLoiTep] = useState<string | undefined>();
  const [trichDoan, setTrichDoan] = useState('');
  const [uuTien, setUuTien] = useState<UuTienPhanCong>('AUTO');
  const [han, setHan] = useState<number>(14);
  const [ghiChu, setGhiChu] = useState('');

  const dangChay = phanCong.isPending;

  const dong = () => {
    if (dangChay) phanCong.huy();
    onClose();
  };

  const chonTep = (f: File) => {
    if (!(DUOI_TEP_PHAN_CONG as readonly string[]).includes(duoi(f.name))) {
      setLoiTep(t('vanBanPhanCong.loi.dinhDang'));
      return;
    }
    if (f.size > TOI_DA_MB * 1024 * 1024) {
      setLoiTep(t('vanBanPhanCong.loi.tepQuaLon', { mb: TOI_DA_MB }));
      return;
    }
    setLoiTep(undefined);
    setTep(f);
    setTrichDoan('');
    if (/\.(txt|md)$/i.test(f.name)) {
      void f.text().then((s) => setTrichDoan(thuGon(s)));
      return;
    }
    trich.mutate(f, {
      onSuccess: (r) => setTrichDoan(thuGon(r.text)),
      onError: () => setTrichDoan(t('vanBanPhanCong.tep.khongTrichDuoc', { ten: f.name })),
    });
  };

  const chay = () => {
    if (!tep) return;
    phanCong.mutate(
      { file: tep, priority: uuTien, default_deadline_days: han, description: ghiChu.trim() },
      {
        onSuccess: (kq) => {
          if (kq === 'da-huy') return;
          if (kq === 'het-gio') {
            toast.error(t('vanBanPhanCong.tep.hetGio'));
            return;
          }
          if (kq.status === 'failed' || kq.status === 'not_found') {
            // Câu lỗi của backend là dữ liệu, giữ nguyên văn
            toast.error(kq.message || t('vanBanPhanCong.tep.thatBai'));
            return;
          }
          const so = kq.created_count ?? kq.tasks?.length ?? 0;
          if (so === 0) {
            toast.warning(kq.message || t('vanBanPhanCong.tep.khongTaoDuoc'));
            return;
          }
          toast.success(t('vanBanPhanCong.tep.daTao', { so: soVN(so, locale), ten: tep.name }));
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={dong}
      width={620}
      destroyOnHidden
      title={
        <span className="flex items-center gap-2">
          <UploadOutlined className="text-primary-500" />
          {t('vanBanPhanCong.tep.tieuDe')}
        </span>
      }
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={dong}>
            {t('vanBanChung.dong')}
          </Button>
          <Button
            variant="primary"
            className="ai-glow"
            icon={dangChay ? <LoadingOutlined /> : <Sparkles size={14} className="ai-sparkle" />}
            disabled={!tep || trich.isPending || dangChay}
            onClick={chay}
          >
            {t(dangChay ? 'vanBanPhanCong.tep.dangPhanCong' : 'vanBanPhanCong.tep.nutChay')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 pt-2">
        <LoiForm loi={phanCong.error} />
        <Field label={t('vanBanPhanCong.tep.tep')} required error={loiTep}>
          {(p) => (
            <Upload.Dragger
              showUploadList={false}
              accept={DUOI_TEP_PHAN_CONG.map((d) => `.${d}`).join(',')}
              disabled={dangChay}
              beforeUpload={(f) => {
                chonTep(f);
                return false;
              }}
            >
              <div
                id={p.id}
                aria-describedby={p['aria-describedby']}
                className="flex flex-col items-center gap-2 px-4 py-6 text-center"
              >
                <InboxOutlined className="text-3xl text-primary-500" aria-hidden />
                <p className="text-sm font-semibold text-primary-700">{t('vanBanPhanCong.tep.keoTha')}</p>
                <p className="text-xs text-content-muted">{t('vanBanPhanCong.tep.dinhDang')}</p>
                {tep && (
                  <span className="mt-1 inline-flex min-w-0 max-w-full items-center gap-2 rounded border border-primary-100 bg-surface px-3 py-1.5 text-xs text-primary-700">
                    <FileTextOutlined aria-hidden />
                    <span className="truncate">{tep.name}</span>
                    <span className="shrink-0 text-content-muted">({dungLuongVN(tep.size, locale)})</span>
                  </span>
                )}
              </div>
            </Upload.Dragger>
          )}
        </Field>

        {trich.isPending && (
          <p role="status" className="flex items-center gap-2 text-xs text-content-muted">
            <LoadingOutlined aria-hidden /> {t('vanBanPhanCong.tep.dangTrich')}
          </p>
        )}
        {!trich.isPending && trichDoan && (
          <div className="max-h-24 overflow-y-auto rounded border border-default bg-subtle px-3 py-2 text-xs leading-relaxed text-content-secondary">
            <b className="text-content-primary">{t('vanBanPhanCong.tep.trichDoan')} </b>
            {trichDoan}…
          </div>
        )}

        {phanCong.buoc && (
          <p
            role="status"
            className="flex items-center gap-2 rounded border border-primary-100 bg-primary-50 px-3 py-2 text-xs text-primary-700"
          >
            <LoadingOutlined aria-hidden />
            {t(phanCong.buoc === 'tai-len' ? 'vanBanPhanCong.tep.dangTaiLen' : 'vanBanPhanCong.tep.aiDangXuLy')}
          </p>
        )}

        <div className="space-y-4 rounded-lg border border-default bg-subtle p-4">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-content-primary">
            <RobotOutlined className="text-primary-500" aria-hidden /> {t('vanBanPhanCong.tep.tuyChon')}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('vanBanPhanCong.tep.uuTien')}>
              {(p) => (
                <Select
                  {...p}
                  className="w-full"
                  value={uuTien}
                  disabled={dangChay}
                  onChange={(v) => setUuTien(v as UuTienPhanCong)}
                  options={UU_TIEN_PHAN_CONG.map((value) => ({ value, label: t(NHAN_UU_TIEN[value]) }))}
                />
              )}
            </Field>
            <Field label={t('vanBanPhanCong.tep.hanMacDinh')}>
              {(p) => (
                <Select
                  {...p}
                  className="w-full"
                  value={han}
                  disabled={dangChay}
                  onChange={(v) => setHan(v as number)}
                  options={HAN_MAC_DINH.map((value) => ({
                    value,
                    label: t('vanBanPhanCong.tep.soNgay', { so: soVN(value, locale) }),
                  }))}
                />
              )}
            </Field>
          </div>
          <Field label={t('vanBanPhanCong.tep.ghiChu')}>
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                disabled={dangChay}
                placeholder={t('vanBanPhanCong.tep.ghiChuGoiY')}
                value={ghiChu}
                onChange={(e) => setGhiChu(e.target.value)}
              />
            )}
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
